<template>
	<CToolTip
		:placement="'right-start'"
		:use-button="false"
		:is-interactive="false"
		:disabled="!disabled"
		class="organization-logo-upload-tooltip"
	>
		{{ disabledTooltip }}
		<template #button>
			<div class="upload" @click="handleLogoClick">
				<button v-if="hasLogo" title="Delete logo" class="upload__delete button--no-style bg-shade-11 text-shade-1 sz-5" :disabled="disabled" @click.stop="deleteLogo">
					<Inline svg="ex" />
				</button>
				<ElUpload
					:class="[{ loading: isLoading }, disabled ? 'pointer-events-none opacity-60' : '']"
					class="flex flex-col items-start"
					action=""
					drag
					:show-file-list="false"
					:http-request="uploadFile"
					:before-upload="beforeLogoUpload"
					accept=".jpeg, .jpg, .png, .svg, .webp"
					:disabled="disabled"
				>
					<div class="upload__image-container">
						<Inline v-if="isLoading" svg="cog" class="spin" />
						<Inline v-else-if="!hasLogo || showUpsell" class="cog-nav-button__cogicon relative" svg="logos/cogicon" />
						<img
							v-else
							:src="url"
							alt="Organization logo"
							class="upload__image"
							@load="loaded"
						/>
						<Inline svg="plus" />
					</div>
					<button class="button button--no-style text-link underline text-center w-full" :disabled="disabled" @click="handleLogoClick">Change</button>
				</ElUpload>
			</div>
		</template>
	</CToolTip>
</template>

<script lang="ts">
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import { defineComponent, ref, computed, watch } from 'vue';
	import { Upload } from '@cognitoforms/element-ui';
	import Inline from 'src/components/content/Inline.vue';
	import notify from 'src/utilities/notify';
	import { setOrgAppearanceLogo, deleteOrgAppearanceLogo } from '@cognitoforms/api/services/settings/org-appearance-service';

	export default defineComponent({
		name: 'OrganizationLogoUpload',
		components: {
			ElUpload: Upload,
			Inline,
			CToolTip
		},
		props: {
			url: {
				type: String,
				default: null
			},
			disabled: {
				type: Boolean,
				default: false
			},
			disabledTooltip: {
				type: String,
				default: 'You cannot upload a logo while on a trial. Please upgrade to upload a logo.'
			},
			showUpsell: {
				type: Boolean,
				default: false
			}
		},
		emits: ['show-upsell'],
		setup(props, { emit }) {
			const isLoading = ref(false);

			const hasLogo = computed(() => !!props.url);

			const beforeLogoUpload = (file: File) => {
				const maxSizeMB = 10;
				const splitFile = file.type.split('/');
				const isImg = splitFile[0] === 'image';
				const isOverMB = file.size / 1024 / 1024 > maxSizeMB;
				const typeOfImage = splitFile[1];
				const isSupported = ['bmp', 'jpeg', 'jpg', 'png', 'svg', 'svg+xml', 'webp'].includes(typeOfImage);
				if (!isImg) {
					notify('Logo must be an image.', 'error');
				}
				else if (!isSupported) {
					notify('This image file type is not supported.', 'error');
				}
				else if (isOverMB) {
					notify('Logo size cannot exceed 10MB.', 'error');
				}
				return isImg && !isOverMB && isSupported;
			};

			watch(() => props.url, ()=>{
				isLoading.value = false;
			});

			const uploadFile = async (options: any) => {
				const file = options.file;
				try {
					isLoading.value = true;
					await setOrgAppearanceLogo(file);
					notify('Logo updated.', 'success');
				}
				catch (err) {
					notify('Failed to update logo.', 'error');
					isLoading.value = false;
				}
			};

			const deleteLogo = async () => {
				isLoading.value = true;
				try {
					await deleteOrgAppearanceLogo();
					notify('Logo deleted.', 'success');
				}
				catch (err) {
					notify('Failed to delete logo.', 'error');
				}
				finally {
					isLoading.value = false;
				}
			};

			const loaded = () => {
				isLoading.value = false;
			};

			const handleLogoClick = (event) => {
				if (props.showUpsell) {
					if (event && typeof event.preventDefault === 'function') event.preventDefault();
					if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
					emit('show-upsell');
				}
			};

			return {
				isLoading,
				hasLogo,
				beforeLogoUpload,
				uploadFile,
				deleteLogo,
				loaded,
				handleLogoClick
			};
		}
	});
</script>

<style lang="scss">
@import '@cognitoforms/styles/app/common/upload';

.organization-logo-upload-tooltip {
	max-width: 150px;
}
</style>
